import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DesktopSellerPageContainer } from '@/components/desktop/DesktopSellerPageContainer';
import { EventCard } from '@/components/mypage/EventCard';
import { useToast } from '@/components/common/Toast';
import { ProtectedRoute } from '@/components/common/ProtectedRoute';
import { useLoadingCoordinator } from '@/hooks/useLoadingCoordinator';
import { sellerEventsService } from '@/services/sellerEvents';
import type { Event } from '@/types';

export function DesktopSellerEventsPage() {
  const navigate = useNavigate();
  const { showToast, ToastContainer } = useToast();
  const [events, setEvents] = useState<Event[]>([]);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'active' | 'upcoming'>('all');

  const { 
    shouldShowLoading, 
    shouldShowSkeleton, 
    shouldShowContent 
  } = useLoadingCoordinator(isDataLoading, {
    useSkeletonMode: true,
    minLoadingTime: 200
  });

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setIsDataLoading(true);
        const result = filter === 'active' 
          ? await sellerEventsService.getActiveEvents()
          : await sellerEventsService.getAllEvents();
        
        if (result.success && result.data) {
          let filteredEvents = result.data;
          
          if (filter === 'upcoming') {
            const now = new Date();
            filteredEvents = result.data.filter(event => new Date(event.start_date) > now);
          }
          
          // Transform seller events to match Event type
          const transformedEvents: Event[] = filteredEvents.map(event => ({
            id: event.id,
            name: event.name,
            description: event.description || '',
            event_type: 'seller_event',
            start_date: event.start_date,
            end_date: event.end_date,
            max_participants: undefined,
            current_participants: 0,
            reward_points: 0,
            reward_coupon_id: undefined,
            season: undefined,
            is_active: event.is_active,
            terms_and_conditions: undefined,
            created_at: event.created_at,
            updated_at: event.updated_at,
            image_url: undefined,
            summary: event.description || ''
          }));
          
          setEvents(transformedEvents);
        } else {
          showToast(result.error || '이벤트를 불러오는데 실패했습니다');
        }
      } catch (error) {
        console.error('Error fetching events:', error);
        showToast('이벤트를 불러오는데 실패했습니다');
      } finally {
        setIsDataLoading(false);
      }
    };

    fetchEvents();
  }, [filter, showToast]);

  const getFilterText = (filterType: typeof filter) => {
    switch (filterType) {
      case 'all': return '전체';
      case 'active': return '진행중';
      case 'upcoming': return '예정';
      default: return '전체';
    }
  };

  // 전체 화면 로딩 (인증 로딩)
  if (shouldShowLoading) {
    return (
      <ProtectedRoute requiredRole="seller" minimalLoading={true}>
        <DesktopSellerPageContainer>
          <div className="max-w-4xl mx-auto">
            <div className="mb-8 flex items-center">
              <button
                onClick={() => navigate(-1)}
                className="p-2 text-gray-600 hover:text-gray-800 mr-4"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <h1 className="text-[32px] font-bold text-gray-800">이벤트</h1>
            </div>
            <div className="flex items-center justify-center min-h-[600px]">
              <div className="text-center">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-green-500 mx-auto mb-6"></div>
                <p className="text-[18px] text-gray-600">이벤트를 불러오는 중...</p>
              </div>
            </div>
          </div>
        </DesktopSellerPageContainer>
      </ProtectedRoute>
    );
  }

  // 스켈레톤 로딩 (데이터 로딩)
  if (shouldShowSkeleton) {
    return (
      <ProtectedRoute requiredRole="seller" minimalLoading={true}>
        <DesktopSellerPageContainer>
          <div className="max-w-4xl mx-auto">
            {/* Page Header */}
            <div className="mb-8 flex items-center">
              <button
                onClick={() => navigate(-1)}
                className="p-2 text-gray-600 hover:text-gray-800 mr-4"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <h1 className="text-[32px] font-bold text-gray-800">이벤트</h1>
            </div>
            
            {/* 필터 탭 스켈레톤 */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 mb-8">
              <div className="flex space-x-2 bg-gray-100 rounded-lg p-2">
                {[1, 2, 3].map((_, index) => (
                  <div key={index} className="flex-1 py-3 px-6">
                    <div className="h-5 bg-gray-200 rounded animate-pulse"></div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* 이벤트 카드 스켈레톤 */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {[1, 2, 3, 4].map((_, index) => (
                  <div key={index} className="p-6 border border-gray-200 rounded-lg">
                    <div className="animate-pulse">
                      <div className="h-6 bg-gray-200 rounded mb-3"></div>
                      <div className="h-4 bg-gray-200 rounded w-3/4 mb-3"></div>
                      <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </DesktopSellerPageContainer>
      </ProtectedRoute>
    );
  }

  // 콘텐츠 표시
  if (shouldShowContent) {
    return (
      <ProtectedRoute requiredRole="seller" minimalLoading={true}>
        <DesktopSellerPageContainer>
          <div className="max-w-4xl mx-auto">
            {/* Page Header */}
            <div className="mb-8 flex items-center">
              <button
                onClick={() => navigate(-1)}
                className="p-2 text-gray-600 hover:text-gray-800 mr-4"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <h1 className="text-[32px] font-bold text-gray-800">이벤트</h1>
            </div>

            {/* 필터 탭 */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 mb-8">
              <div className="flex space-x-2 bg-gray-100 rounded-lg p-2">
                {(['all', 'active', 'upcoming'] as const).map((filterType) => (
                  <button
                    key={filterType}
                    onClick={() => setFilter(filterType)}
                    className={`flex-1 py-3 px-6 text-[16px] font-medium rounded-lg transition-colors ${
                      filter === filterType
                        ? 'bg-white text-gray-800 shadow-sm'
                        : 'text-gray-600 hover:text-gray-800'
                    }`}
                  >
                    {getFilterText(filterType)}
                  </button>
                ))}
              </div>
            </div>

            {/* 이벤트 목록 */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              {events.length === 0 ? (
                <div className="flex items-center justify-center py-20">
                  <div className="text-center">
                    <div className="w-20 h-20 mx-auto mb-6 text-gray-300">
                      <svg width="80" height="80" viewBox="0 0 24 24" fill="none">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
                        <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        <line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        <path d="M8 14H12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        <path d="M8 18H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    </div>
                    <p className="text-[20px] text-gray-600 mb-3">
                      {filter === 'active' && '진행 중인 이벤트가 없습니다'}
                      {filter === 'upcoming' && '예정된 이벤트가 없습니다'}
                      {filter === 'all' && '등록된 이벤트가 없습니다'}
                    </p>
                    <p className="text-[16px] text-gray-500">새로운 이벤트가 등록되면 알려드릴게요</p>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {events.map((event) => (
                    <div key={event.id} className="h-full">
                      <EventCard event={event} />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Information Section */}
            <div className="mt-8 bg-blue-50 rounded-xl p-6 border border-blue-200">
              <div className="flex items-start">
                <div className="w-6 h-6 text-blue-600 mr-3 mt-1">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-[18px] font-semibold text-blue-800 mb-2">이벤트 안내</h3>
                  <ul className="text-[16px] text-blue-700 space-y-1">
                    <li>• 이벤트는 관리자가 등록하며, 판매자님께 혜택을 제공합니다</li>
                    <li>• 진행 중인 이벤트에 참여하여 다양한 혜택을 받아보세요</li>
                    <li>• 이벤트 관련 문의사항은 고객센터로 연락해 주세요</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          
          <ToastContainer />
        </DesktopSellerPageContainer>
      </ProtectedRoute>
    );
  }

  // 폴백 (예상되지 않는 상태)
  return null;
}